import { useEffect, useState } from "react";

import { AlertTriangle, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogInner,
  DialogTitle,
} from "@/components/ui/dialog";
import { SITE_NAME } from "@/css/constants";
import { useRemoveAssignmentMutation } from "@/graphql";
import { Assignment } from "@/types/coaches";
import { formatErrors } from "@/utils/array";

import { AssignmentCoach } from "./CoachMenu";

interface RemoveCoachProps {
  showingWarning: boolean;
  onCancel: () => void;
  assignment: Assignment;
  coach: AssignmentCoach;
  viewAsCoach: boolean;
  userId: string;
  onRemove?: () => void;
}

export function RemoveCoach({
  showingWarning,
  onCancel,
  onRemove,
  assignment,
  coach,
}: RemoveCoachProps) {
  const [errors, setErrors] = useState<string>();

  const [removeCoach, { loading }] = useRemoveAssignmentMutation({
    variables: {
      id: assignment.id,
    },
    onCompleted: (response) => {
      const { errors, ok } = response.removeAssignment || {};
      if (!ok) {
        const errorMessage = formatErrors(errors);
        setErrors(errorMessage);
      } else {
        onCancel();
        onRemove?.();
      }
    },
    refetchQueries: ["CoachingDashboard"],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (showingWarning) {
      setErrors(undefined);
    }
  }, [showingWarning]);

  if (errors) {
    return (
      <Dialog open={showingWarning} onOpenChange={() => onCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Something went wrong
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <div>
                You cannot remove this coach because you have at least one order
                with this coach.
              </div>
              <div>Please contact {SITE_NAME} rep for more information.</div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" onClick={onCancel}>
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={showingWarning} onOpenChange={() => onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-">
            Are you sure you want to remove {coach.name}?
          </DialogTitle>
        </DialogHeader>
        <DialogInner className="space-y-4">
          <ul className="space-y-3 pl-4 list-disc">
            <li>
              This <strong>will</strong> close your DM channel with {coach.name}
            </li>
            <li>
              This <strong>will not</strong> cancel any upcoming meetings or
              ongoing reviews
            </li>
            <li>
              {coach.name} <strong>will</strong> be able to contact you about
              upcoming meetings or ongoing reviews
            </li>
          </ul>
        </DialogInner>
        <DialogFooter>
          <Button intent="danger" onClick={() => removeCoach()}>
            Remove Coach
          </Button>
          <DialogClose asChild>
            <Button variant="outline" intent="secondary" onClick={onCancel}>
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
        {loading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-50">
            <Loader2 className="h-6 w-6 animate-spin text-red-600" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
